import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { JssService } from '@app/shared/services/jss.service';
import { MATERIAL_OPTIONS } from '../jss-creation/material-option';

@Component({
  selector: 'app-jss-list',
  templateUrl: './jss-list.component.html',
  styleUrls: ['./jss-list.component.css']
})
export class JssListComponent implements OnInit {
  jssList: any[] = [];
  currentPage: number = 1;
  itemsPerPage: number = 15;
  searchText: string = '';
  updateJssForm: FormGroup;
  showTable: boolean = true;
  selectedItemId: string | null = null;
  itemId: string;
  materialOptions = MATERIAL_OPTIONS;
  selectedStatus: string = ''; // New property for selected status
  filteredJssList: any[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private jss: JssService,
    private cdr: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.getJssList();
    this.activatedRoute.params.subscribe((params) => {
      console.log('Route Params:', params);
      const itemId = params['id'];
      if (itemId) {
        this.itemId = itemId;
        this.updatejss(this.itemId);
        this.showTable = false;
      }
    });
  }

  initForm(): void {
    this.updateJssForm = this.formBuilder.group({
      clientName: [''],
      ticketno: [''],
      jssfolderno: [''],
      poNo: [''],
      refNo: [''],
      coilWidth: [''],
      coilod: [''],
      printType: [''],
      repeatLength: [''],
      printDirection: [''],
      std: [''],
      pouch: [''],
      totalGsm: [''],
      colorNames: [''],
      colorNumber: [''],
      jobName: [''],
      jobType: [''],
      orderKgs: [''],
      LamType: [''],
      coreType: [''],
      liquid: [''],
      inkType: [''],
      barcode: [''],
      cylinders: this.formBuilder.group({
        id: [''],
        supplier: [''],
        length: [''],
        circumference: [''],
        location: [''],
      }),
      materials: this.formBuilder.array([]),
    });
  }

  addMaterial() {
    const materialGroup = this.formBuilder.group({
      material: ['', [Validators.required]],
      thickness: ['', [Validators.required]],
      width: ['', [Validators.required]],
      selectedProcess: ['', [Validators.required]],
    });
    this.materials.push(materialGroup);
  }

  removeMaterial(index: number) {
    if (this.materials.length > 1) {
      this.materials.removeAt(index);
    } else {
      alert('At least one material must be present.');
    }
  }

  get materials(): FormArray {
    return this.updateJssForm.get('materials') as FormArray;
  }

  getJssList() {
    this.jss.getJssList().subscribe(
      (res: any) => {
        this.jssList = res;
        this.jssList.reverse();
        this.filteredJssList = [...this.jssList];
        this.cdr.detectChanges();
      },
      (error) => {
        console.error('Error fetching JSS list:', error);
      }
    );
  }

  filterByStatus() {
    if (this.selectedStatus === '') {
      this.filteredJssList = this.jssList; // Show all items
    } else {
      this.filteredJssList = this.jssList.filter(item => item.status === this.selectedStatus);
    }
  }

  toggleView() {
    this.showTable = !this.showTable;
    this.selectedItemId = null;
    this.updateJssForm.reset();
  }

  updatejss(id: any) {
    this.jss.getJssById(id).subscribe(
      (res: any) => {
        console.log(res);
        this.updateJssForm.patchValue({
          clientName: res.clientName,
          ticketno: res.ticketno,
          poNo: res.poNo,
          refNo: res.refNo,
          ddDate: res.ddDate,
          coilWidth: res.coilWidth,
          coilod: res.coilod,
          pouch: res.orderPcs ? res.orderPcs : 0,
          jobName: res.jobName,
          jobType: res.jobType,
          orderKgs: res.orderKgs,
          remark: res.remark,
          fdOption: res.fdOption,
          packageType: res.packageType,
          barcode: res.barcode,
        });

        // Clear existing materials before adding new ones
        this.materials.clear();

        // Assuming res contains an array of materials
        const materialsArray = [
          { material: res.sMat, thickness: res.thickness, width: res.width },
          { material: res.sMat2, thickness: res.thickness2, width: res.width2 },
          { material: res.sMat3, thickness: res.thickness3, width: res.width3 },
        ];

        materialsArray.forEach(mat => {
          const materialGroup = this.formBuilder.group({
            material: [mat.material, [Validators.required]],
            thickness: [mat.thickness, [Validators.required]],
            width: [mat.width, [Validators.required]],
            selectedProcess: ['', [Validators.required]],
          });
          this.materials.push(materialGroup);
        });
      },
      (err: any) => {
        console.log(err);
      }
    );
  }

  submitForm(): void {
    if (this.updateJssForm.valid) {
      const formData = this.updateJssForm.value;
      const id = this.itemId;
      this.jss.updateJss(id, formData).subscribe(
        (res: any) => {
          console.log('Data updated successfully:', res);
          this.updateJssForm.reset();
          this.router.navigate(['/jsslist']);
        },
        (error) => {
          console.error('Error updating data:', error);
        }
      );
    } else {
      console.error('Form is invalid!');
    }
  }

  // Pagination Methods
  updateDisplayedJss() {
    const filteredJss = this.filteredJssList;
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.jssList = filteredJss.slice(startIndex, endIndex);
  }

  getPages(): number[] {
    const totalPages = Math.ceil(this.filteredJssList.length / this.itemsPerPage);
    const maxPagesToShow = 1; // Number of pages to show around the current page

    // Start and end page number for the window
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, this.currentPage + Math.floor(maxPagesToShow / 2));

    // Adjust the range to ensure it's at least showing maxPagesToShow pages if possible
    if (endPage - startPage + 1 < maxPagesToShow) {
      if (this.currentPage < Math.floor(maxPagesToShow / 2)) {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      } else {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
    }

    return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= Math.ceil(this.filteredJssList.length / this.itemsPerPage)) {
      this.currentPage = page;
      this.updateDisplayedJss();
    }
  }

  onSearch(): void {
    this.currentPage = 1; // Reset to the first page when performing a new search
    this.updateDisplayedJss();
  }

  deleteJss(itemId: string) {
    if (confirm('Are you sure you want to delete this JSS data?')) {
      this.jss.deleteJss(itemId).subscribe(() => {
        this.jssList = this.jssList.filter(repeatedorder => repeatedorder._id !== itemId);
      }, error => {
        console.error('Error while deleting this entry:', error);
      });
    }
  }

  private matchesSearch(client: any): boolean {
    const searchFields = ['clientName', 'email', 'contactNo'];
    const lowerCaseSearch = this.searchText.toLowerCase();

    return searchFields.some((field) => {
      const fieldValue = client[field];
      if (fieldValue && typeof fieldValue === 'string') {
        return fieldValue.toLowerCase().includes(lowerCaseSearch);
      }
      return false;
    });
  }

  selectedItemId2: string | null = null;

  onCheckboxChange(item: any) {
    if (this.selectedItemId2 === item._id) {
      this.selectedItemId2 = null;
    } else {
      this.selectedItemId2 = item._id;
      console.log(`Selected item ID: ${item._id}`);
    }
  }

  deleteSelectedEntries() {
    const checkboxes = document.querySelectorAll('.rowCheckbox');
    const selectedIds = [];

    checkboxes.forEach((checkbox) => {
      if ((checkbox as HTMLInputElement).checked) {
        selectedIds.push((checkbox as HTMLInputElement).dataset.id);
      }
    });

    if (selectedIds.length > 0) {
      this.jss.deleteAllJss(selectedIds).subscribe(
        (response) => {
          console.log('Entries deleted successfully:', response);
          this.getJssList();
        },
        (error) => {
          console.error('Error deleting entries:', error);
        }
      );
    } else {
      console.log('No entries selected for deletion');
    }
  }

  selectAllChanged(event: Event) {
    const checkboxes = document.querySelectorAll('.rowCheckbox');
    checkboxes.forEach((checkbox) => {
      (checkbox as HTMLInputElement).checked = (event.target as HTMLInputElement).checked;
    });
  }
}
